const _excluded = ["editorHeight", "placeholder", "initialValue", "error", "helperText", "className", "editorRef", "onChange"];

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import React, { useEffect, useRef, useState } from 'react';
import Quill from 'quill';
import cn from 'classnames';
import { Typography } from "../Typography";
import styles from "./RichTextEditor.module.scss";
/** Returns a pure-text string from a quill rich text object. */

export const extractPureTextFromRichText = function extractPureTextFromRichText() {
  let richText = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
  const quill = new Quill(document.createElement('div'), {
    modules: {
      toolbar: false
    },
    theme: 'snow'
  });
  applyValueToQuill(quill, richText);
  return quill.getText();
};
export const applyValueToQuill = (quill, value) => {
  if (value) {
    if (typeof value === 'object') {
      // We assume it's a rich-text object.
      quill.setContents(value.value);
    } else {
      try {
        // Maybe its a stringified rich-text object.
        const parsed = JSON.parse(value);

        if (parsed.value) {
          quill.setContents(parsed.value);
        } else {
          // Sometimes we can parse a string of pure text. For example, when it contains only digits.
          // In that case, we want to assume its pure text.
          quill.setText(value);
        }
      } catch (err) {
        // We assume its pure text.
        quill.setText(value);
      }
    }
  } else {
    quill.setText('');
  }
};
/** Rich text is always saved as a stringified object. This is the object interface once parsed. */

const Link = Quill.import('formats/link');

class CustomLinkSanitizer extends Link {
  static sanitize(url) {
    // Run default sanitize method from Quill
    const sanitizedUrl = super.sanitize(url); // Blank is not a whitelisted URL based on protocol so let's short-circuit here

    if (!sanitizedUrl || sanitizedUrl === 'about:blank') return sanitizedUrl; // Verify whether the URL already has a whitelisted protocol

    const hasWhitelistedProtocol = this.PROTOCOL_WHITELIST.some(function (protocol) {
      return sanitizedUrl.startsWith("".concat(protocol, "://"));
    });
    if (hasWhitelistedProtocol) return sanitizedUrl; // if not, then prepend 'https' to force the URL to be absolute

    return "https://".concat(sanitizedUrl);
  }

}

_defineProperty(CustomLinkSanitizer, "PROTOCOL_WHITELIST", ['http', 'https', 'mailto', 'tel', 'sms']);

Quill.register(CustomLinkSanitizer, true);
export function RichTextEditor(props) {
  const {
    editorHeight,
    placeholder,
    initialValue = '',
    error,
    helperText,
    className,
    editorRef,
    onChange
  } = props,
        rest = _objectWithoutProperties(props, _excluded);

  const instance = useRef({
    onChange
  });
  instance.current = {
    onChange
  };
  const rootRef = useRef(null);
  const hasQuill = useRef(false);
  const [editorEl, setEditorEl] = useState(null);
  useEffect(() => {
    if (!hasQuill.current && editorEl) {
      const quill = new Quill(editorEl, {
        modules: {
          toolbar: [[{
            header: [1, 2, false]
          }], ['bold', 'italic', 'underline', 'strike'], [{
            list: 'ordered'
          }, {
            list: 'bullet'
          }, {
            indent: '-1'
          }, {
            indent: '+1'
          }], ['link'], [{
            color: []
          }], [{
            background: []
          }]]
        },
        placeholder: placeholder || '',
        theme: 'snow',
        // Important for tooltips not to open beyond the editor bounds and behave
        // well when the editor is in a modal.
        bounds: rootRef.current,
        formats: ['background', 'bold', 'color', 'italic', 'link', 'strike', 'underline', 'header', 'indent', 'list']
      });
      hasQuill.current = true;

      if (props['data-testid']) {
        quill.root.setAttribute('data-testid', props['data-testid'] + '-input');
      }

      applyValueToQuill(quill, initialValue);
      quill.on('text-change', () => {
        const newContent = quill.getContents();
        instance.current.onChange && instance.current.onChange({
          type: 'quill-delta',
          value: newContent
        });
      });
    }
  }, [editorEl]);
  return /*#__PURE__*/React.createElement("div", _extends({
    className: cn(styles.root, error && styles.error, className),
    ref: rootRef
  }, rest), /*#__PURE__*/React.createElement("div", {
    style: {
      height: editorHeight || 200
    },
    ref: el => {
      editorRef && editorRef(el);
      setEditorEl(el);
    }
  }), helperText && /*#__PURE__*/React.createElement(Typography, {
    variant: "paragraph3",
    skin: error ? 'error' : 'secondary',
    className: styles.helperText,
    "data-testid": props['data-testid'] && props['data-testid'] + '-helper-text'
  }, helperText));
}